import * as React from 'react';

import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  useSensor,
  useSensors,
  KeyboardSensor,
  UniqueIdentifier,
  TouchSensor,
  MouseSensor,
  Active,
  DataRef,
  Over,
} from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { getRemRankValue } from '@rematter/ui-web';
import { createPortal } from 'react-dom';

import { HaulerDriversSection } from './HaulerDriversSection';
import {
  type ColumnDragData,
  KanbanColumn,
  KanbanBoardContainer,
  PinnedVerticalColumn,
} from './KanbanColumn';
import type { TKanbanColumn } from './KanbanColumn';
import {
  type KanbanTask,
  type TaskDragData,
  type RouteGroupDragData,
  TaskCard,
} from './KanbanTask';
import { coordinateGetter } from './multipleContainersKeyboardPreset';

export interface KanbanBoardProps {
  columns: TKanbanColumn[];
  pinnedColumns?: TKanbanColumn[];
  haulers?: { id: string; name: string }[];
  tasks: KanbanTask[];
  pinnedEmptyMessage?: string;
  pinnedCollapsed?: boolean;
  onTogglePinned?: () => void;
  onTaskClick?: (taskId: UniqueIdentifier) => void;
  onRouteClick?: (routeId: string) => void;
  onTaskMove?: (
    columnId: string,
    taskId: string,
    newRank: string,
    tasks: KanbanTask[],
  ) => void;
  onRouteGroupMove?: (
    columnId: string,
    routeId: string,
    taskIds: string[],
  ) => void;
  onRouteReorder?: (routeId: string, orderedTaskIds: string[]) => void;
  onSubmitForDriver?: (driverId: string) => void;
  onColumnReorder?: (columnIds: string[]) => void;
}

export const KanbanBoard = ({
  columns: columnsProp,
  pinnedColumns = [],
  haulers = [],
  tasks: initialTasks,
  pinnedEmptyMessage,
  pinnedCollapsed,
  onTogglePinned,
  onTaskClick,
  onRouteClick,
  onTaskMove,
  onRouteGroupMove,
  onRouteReorder,
  onSubmitForDriver,
  onColumnReorder,
}: KanbanBoardProps) => {
  const [columns, setColumns] = React.useState<TKanbanColumn[]>(columnsProp);
  const columnsId = React.useMemo(
    () => columns.map((col) => col.id),
    [columns],
  );

  React.useEffect(() => {
    setColumns((prev) => {
      const prevIds = prev.map((c) => String(c.id));
      const newIds = columnsProp.map((c) => String(c.id));
      const sameIds =
        prevIds.length === newIds.length &&
        prevIds.every((id) => newIds.includes(id));
      if (sameIds) {
        const byId = new Map(columnsProp.map((c) => [String(c.id), c]));
        return prev.map((c) => byId.get(String(c.id)) ?? c);
      }
      return columnsProp;
    });
  }, [columnsProp]);

  const [tasks, setTasks] = React.useState<KanbanTask[]>([]);

  const [activeColumn, setActiveColumn] = React.useState<TKanbanColumn | null>(
    null,
  );
  const [targetColumnId, setTargetColumnId] =
    React.useState<UniqueIdentifier | null>(null);
  const [activeTask, setActiveTask] = React.useState<KanbanTask | null>(null);
  const [activeRouteGroup, setActiveRouteGroup] =
    React.useState<RouteGroupDragData | null>(null);

  const pendingRouteReorderRef = React.useRef<{
    routeId: string;
    orderedTaskIds: string[];
  } | null>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    }),
  );

  React.useEffect(() => {
    setTasks(initialTasks);
  }, [initialTasks]);

  const handleTaskClick = (taskId: UniqueIdentifier) => {
    onTaskClick?.(taskId);
  };

  const handleDragStart = (event: DragStartEvent) => {
    pendingRouteReorderRef.current = null;
    if (!hasDraggableData(event.active)) return;
    const data = event.active.data.current;
    if (data?.['type'] === 'Column') {
      setActiveColumn(data['column']);
      return;
    }

    if (data?.['type'] === 'RouteGroup') {
      setActiveRouteGroup(data as RouteGroupDragData);
      return;
    }

    if (data?.['type'] === 'Task') {
      setActiveTask(data['task']);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    const draggedRouteGroup = activeRouteGroup;

    setActiveColumn(null);
    setActiveTask(null);
    setActiveRouteGroup(null);
    setTargetColumnId(null);

    if (!over || !hasDraggableData(active)) {
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    const activeData = active.data.current;
    const isActiveATask = activeData?.['type'] === 'Task';
    const isActiveARouteGroup = activeData?.['type'] === 'RouteGroup';

    if (isActiveARouteGroup && draggedRouteGroup && targetColumnId) {
      onRouteGroupMove?.(
        String(targetColumnId),
        draggedRouteGroup.routeId,
        draggedRouteGroup.tasks.map((t) => String(t.id)),
      );
      return;
    }

    if (isActiveATask) {
      if (activeTask) {
        const pending = pendingRouteReorderRef.current;
        if (
          pending &&
          activeTask.routeId &&
          pending.routeId === activeTask.routeId
        ) {
          onRouteReorder?.(pending.routeId, pending.orderedTaskIds);
          pendingRouteReorderRef.current = null;
          return;
        }

        const newTaskRank = getRemRankValue<KanbanTask>(
          tasks,
          tasks.findIndex((t) => t.id === activeTask.id),
          (t) => t.rank,
        );

        if (targetColumnId) {
          onTaskMove?.(
            String(targetColumnId),
            String(activeTask.id),
            newTaskRank,
            tasks,
          );
        }
      }
      return;
    }

    if (activeId === overId) return;

    setColumns((columns) => {
      const activeColumnIndex = columns.findIndex((col) => col.id === activeId);
      const overColumnIndex = columns.findIndex((col) => col.id === overId);

      const reordered = arrayMove(columns, activeColumnIndex, overColumnIndex);
      onColumnReorder?.(reordered.map((col) => String(col.id)));
      return reordered;
    });
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    if (!hasDraggableData(active) || !hasDraggableData(over)) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const isActiveATask = activeData?.['type'] === 'Task';
    const isActiveARouteGroup = activeData?.['type'] === 'RouteGroup';
    const isOverATask = overData?.['type'] === 'Task';
    const isOverAColumn = overData?.['type'] === 'Column';

    if (isActiveARouteGroup) {
      const routeGroup = activeData as RouteGroupDragData;
      const groupTaskIds = new Set(routeGroup.tasks.map((t) => String(t.id)));

      if (isOverATask) {
        const overTask = (overData as TaskDragData).task;
        setTimeout(() =>
          setTasks((tasks) => {
            const firstGroupTask = tasks.find((t) =>
              groupTaskIds.has(String(t.id)),
            );
            if (
              firstGroupTask &&
              firstGroupTask.columnId !== overTask.columnId
            ) {
              setTargetColumnId(overTask.columnId);
              return tasks.map((t) =>
                groupTaskIds.has(String(t.id))
                  ? { ...t, columnId: overTask.columnId }
                  : t,
              );
            }
            setTargetColumnId(firstGroupTask?.columnId ?? null);
            return tasks;
          }),
        );
      } else if (isOverAColumn) {
        setTimeout(() =>
          setTasks((tasks) => {
            const firstGroupTask = tasks.find((t) =>
              groupTaskIds.has(String(t.id)),
            );
            if (firstGroupTask && firstGroupTask.columnId !== overId) {
              setTargetColumnId(overId);
              return tasks.map((t) =>
                groupTaskIds.has(String(t.id)) ? { ...t, columnId: overId } : t,
              );
            }
            setTargetColumnId(firstGroupTask?.columnId ?? null);
            return tasks;
          }),
        );
      }
      return;
    }

    if (!isActiveATask) return;

    if (isActiveATask && isOverATask) {
      setTimeout(() =>
        setTasks((tasks) => {
          const activeIndex = tasks.findIndex((t) => t.id === activeId);
          const overIndex = tasks.findIndex((t) => t.id === overId);
          const activeTask = tasks[activeIndex];
          const overTask = tasks[overIndex];

          if (
            activeTask &&
            overTask &&
            activeTask.columnId !== overTask.columnId
          ) {
            pendingRouteReorderRef.current = null;

            const newTasks = tasks.map((task, index) => {
              if (index === activeIndex) {
                return { ...task, columnId: overTask.columnId };
              }
              return task;
            });

            setTargetColumnId(overTask.columnId);

            const updatedTasks = arrayMove(
              newTasks,
              activeIndex,
              Math.max(overIndex - 1, 0),
            );

            return updatedTasks;
          }

          setTargetColumnId(activeTask?.columnId);

          if (activeTask?.routeId && activeTask.routeId === overTask?.routeId) {
            const routeTasks = tasks
              .filter((t) => t.routeId === activeTask.routeId)
              .sort((a, b) => (a.routePosition ?? 0) - (b.routePosition ?? 0));

            const routeActiveIdx = routeTasks.findIndex(
              (t) => t.id === activeId,
            );
            const routeOverIdx = routeTasks.findIndex((t) => t.id === overId);
            const reorderedRoute = arrayMove(
              routeTasks,
              routeActiveIdx,
              routeOverIdx,
            );

            const posMap = new Map<UniqueIdentifier, number>();
            reorderedRoute.forEach((t, i) => posMap.set(t.id, i));

            const reordered = tasks.map((t) =>
              posMap.has(t.id)
                ? { ...t, routePosition: posMap.get(t.id) ?? 0 }
                : t,
            );

            pendingRouteReorderRef.current = {
              routeId: activeTask.routeId,
              orderedTaskIds: reorderedRoute.map((t) => String(t.id)),
            };
            return reordered;
          }

          return arrayMove(tasks, activeIndex, overIndex);
        }),
      );
    }

    if (isActiveATask && isOverAColumn) {
      setTimeout(() =>
        setTasks((tasks) => {
          pendingRouteReorderRef.current = null;

          const activeIndex = tasks.findIndex((t) => t.id === activeId);
          const activeTask = tasks[activeIndex];

          if (activeTask) {
            const columnChanged = activeTask.columnId !== overId;

            const newTasks = tasks.map((task, index) => {
              if (index === activeIndex) {
                return { ...task, columnId: overId };
              }
              return task;
            });
            const updatedTasks = arrayMove(newTasks, activeIndex, activeIndex);

            if (columnChanged) {
              setTargetColumnId(overId);
            }

            return updatedTasks;
          }
          return tasks;
        }),
      );
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="flex size-full flex-col">
        <div className="flex flex-1 overflow-hidden">
          {pinnedColumns.map((col) => (
            <PinnedVerticalColumn
              key={col.id}
              column={col}
              tasks={tasks.filter((task) => task.columnId === col.id)}
              onTaskClick={handleTaskClick}
              onRouteClick={onRouteClick}
              emptyMessage={pinnedEmptyMessage}
              collapsed={pinnedCollapsed}
              onToggle={onTogglePinned}
            />
          ))}

          <div className="min-w-0 flex-1">
            <KanbanBoardContainer>
              <SortableContext items={columnsId}>
                {columns.map((col) => (
                  <KanbanColumn
                    key={col.id}
                    column={col}
                    tasks={tasks.filter((task) => task.columnId === col.id)}
                    onTaskClick={handleTaskClick}
                    onRouteClick={onRouteClick}
                    onSubmitForDriver={onSubmitForDriver}
                  />
                ))}
              </SortableContext>

              {haulers.map((hauler) => (
                <HaulerDriversSection
                  key={hauler.id}
                  haulerId={hauler.id}
                  haulerName={hauler.name}
                  tasks={tasks}
                  onTaskClick={handleTaskClick}
                  onRouteClick={onRouteClick}
                  onSubmitForDriver={onSubmitForDriver}
                />
              ))}
            </KanbanBoardContainer>
          </div>
        </div>
      </div>

      {createPortal(
        <DragOverlay>
          {activeColumn && (
            <KanbanColumn
              isOverlay
              column={activeColumn}
              tasks={tasks.filter((task) => task.columnId === activeColumn.id)}
            />
          )}
          {activeTask && <TaskCard task={activeTask} isOverlay />}
          {activeRouteGroup && (
            <div className="flex items-center gap-1 rounded-lg border border-primary/20 bg-primary/5 p-1 ring-2 ring-primary">
              <div className="text-[10px] font-semibold text-primary/60 uppercase select-none [writing-mode:vertical-lr]">
                {activeRouteGroup.routeName}
              </div>
              <div className="flex gap-2">
                {activeRouteGroup.tasks.map((task) => (
                  <TaskCard key={task.id} task={task} isOverlay />
                ))}
              </div>
            </div>
          )}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};

const hasDraggableData = <T extends Active | Over>(
  entry: T | null | undefined,
): entry is T & {
  data: DataRef<DraggableData>;
} => {
  if (!entry) {
    return false;
  }

  const data = entry.data.current;

  return Boolean(
    data && ('column' in data || 'task' in data || 'routeId' in data),
  );
};

type DraggableData = ColumnDragData | TaskDragData | RouteGroupDragData;
